import { useState, useEffect, useMemo, useCallback, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from 'ag-grid-react';
import { useAuth } from '../components/auth/AuthContext';
import { InlineMenuItem } from '../components/common/InlineMenu';
import { AI_PROMPT_TEMPLATES } from '../config/aiPromptTemplates';

export interface AiContextMenuOptions {
  /** Object type key — must match a key in AI_PROMPT_TEMPLATES */
  objectType: string;
  /** Ref to the AgGridReact instance (used to look up row data from the DOM) */
  gridRef: RefObject<AgGridReact | null>;
  /** Ref to the container div wrapping the grid (scopes the contextmenu listener) */
  containerRef: RefObject<HTMLDivElement | null>;
  /** Extracts template placeholders from the AG Grid row data */
  getPlaceholders: (rowData: any) => Record<string, string>;
  /** Called after successful clipboard copy */
  onCopied?: () => void;
  /** Called if clipboard copy fails */
  onError?: (err: string) => void;
  /**
   * Optional callback invoked before showing the AI menu.
   * If it returns true, the AI menu is NOT shown (the caller handled the event).
   * The browser context menu is still suppressed either way.
   */
  onBeforeShow?: (rowData: any, event: MouseEvent, cellElement: Element) => boolean;
}

export interface AiContextMenuState {
  /** Whether the InlineMenu should be shown */
  showMenu: boolean;
  /** Position for the InlineMenu */
  menuPosition: { x: number; y: number };
  /** InlineMenuItem array to pass to <InlineMenu items={...}> */
  menuItems: (InlineMenuItem | 'separator')[];
  /** Close handler for <InlineMenu onClose={...}> */
  closeMenu: () => void;
}

function fillTemplate(template: string, placeholders: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (match, key) => placeholders[key] ?? match);
}

export function useAiContextMenu(options: AiContextMenuOptions): AiContextMenuState {
  const { objectType, gridRef, containerRef, getPlaceholders, onCopied, onError, onBeforeShow } = options;
  const { t } = useTranslation();
  const { user } = useAuth();

  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [rowData, setRowData] = useState<any>(null);

  const hasToolkit = Boolean(user?.profile?.mcpKeyId && user?.profile?.mcpKeyIsActive);

  const closeMenu = useCallback(() => {
    setShowMenu(false);
    setRowData(null);
  }, []);

  // Native contextmenu listener — suppresses browser menu and captures row data
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const cellElement = target.closest('.ag-cell');
      if (!cellElement) return;
      if (!containerRef.current?.contains(target)) return;

      e.preventDefault();
      e.stopPropagation();

      const rowElement = cellElement.closest('[role="row"]');
      if (rowElement && gridRef.current?.api) {
        const rowIndex = rowElement.getAttribute('row-index');
        if (rowIndex !== null) {
          const rowNode = gridRef.current.api.getDisplayedRowAtIndex(parseInt(rowIndex));
          if (rowNode) {
            // Let caller handle the event if onBeforeShow returns true
            if (onBeforeShow?.(rowNode.data, e, cellElement)) return;

            setRowData(rowNode.data);
            setMenuPosition({ x: e.clientX, y: e.clientY });
            setShowMenu(true);
          }
        }
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [gridRef, containerRef, onBeforeShow]);

  const menuItems: (InlineMenuItem | 'separator')[] = useMemo(() => {
    const copyPrompt = () => {
      if (!rowData) return;
      const templateConfig = AI_PROMPT_TEMPLATES[objectType];
      if (!templateConfig) return;

      const template = t(templateConfig.i18nKey, { defaultValue: templateConfig.defaultTemplate });
      const placeholders = getPlaceholders(rowData);
      const prompt = fillTemplate(template, placeholders);

      navigator.clipboard.writeText(prompt).then(
        () => onCopied?.(),
        (err) => onError?.(err?.message ?? String(err)),
      );
    };

    if (hasToolkit) {
      return [
        {
          label: t('aiContext.menuLabel', { defaultValue: 'Preguntar a tu asistente de IA' }),
          action: copyPrompt,
        },
      ];
    }

    return [
      {
        label: t('aiContext.menuLabel', { defaultValue: 'Preguntar a tu asistente de IA' }),
        action: () => {},
        disabled: true,
      },
      {
        label: t('aiContext.upsellTooltip', { defaultValue: 'Disponible con FX+ Toolkit' }),
        action: () => {},
        className: 'info-text',
        disabled: true,
      },
    ];
  }, [hasToolkit, rowData, objectType, getPlaceholders, t, onCopied, onError]);

  return {
    showMenu,
    menuPosition,
    menuItems,
    closeMenu,
  };
}
